import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api, { getUserDrones, deleteDrone, getLatestScanResults, performOceanScan, getUserSatellites } from '../services/api';
import './CompoundDefense.css';

const CompoundDefense = () => {
  const { user } = useAuth();
  const [compound, setCompound] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAction, setSelectedAction] = useState('overview');
  const [towerPosition, setTowerPosition] = useState(null);
  const [nearbyThreats, setNearbyThreats] = useState([]);
  const [detectedThreats, setDetectedThreats] = useState([]);
  const [incomingAttacks, setIncomingAttacks] = useState([]);
  const [threatsLoading, setThreatsLoading] = useState(false);
  const [userDrones, setUserDrones] = useState([]);
  const [dronesLoading, setDronesLoading] = useState(false);
  const [lockoutStatus, setLockoutStatus] = useState(null);
  const [satelliteData, setSatelliteData] = useState([]);
  const [userSatellites, setUserSatellites] = useState([]);
  const [satellitesLoading, setSatellitesLoading] = useState(false);
  
  useEffect(() => {
    loadCompoundData();
    checkLockoutStatus();
  }, []);

  useEffect(() => {
    if (selectedAction === 'threats' && compound) {
      // Load threats immediately (will show proper error messages if radar not available)
      loadThreats();
      loadIncomingAttacks();
      
      // Only set up auto-refresh if radar station is active
      if (compound?.radarStation?.active && user?.partsInventory?.some(part => 
        part.name === 'MUP5 Radar Station' && part.category === 'defense_systems'
      )) {
        const threatRefreshInterval = setInterval(() => {
          loadThreats();
          loadIncomingAttacks();
        }, 10000); // 10 seconds for real-time computer movement detection
        
        return () => clearInterval(threatRefreshInterval);
      }
    }
  }, [selectedAction, compound, compound?.radarStation?.active, user?.partsInventory]);

  // Load drones when drones tab is selected
  useEffect(() => {
    if (selectedAction === 'drones') {
      loadUserDrones();
      
      // Set up auto-refresh every 30 seconds for drone status updates
      const droneRefreshInterval = setInterval(() => {
        loadUserDrones();
      }, 30000); // 30 seconds
      
      return () => clearInterval(droneRefreshInterval);
    }
  }, [selectedAction]);

  // Load satellite data when MDSS tab is selected
  useEffect(() => {
    if (selectedAction === 'mdss') {
      loadSatelliteData();
    }
  }, [selectedAction]);
  
  // Update timer for destroyed towers
  useEffect(() => {
    if ((selectedAction === 'defenses' || selectedAction === 'threats') && compound) {
      const hasDestroyedTowers = compound.defenseTowers.some(t => t.health <= 0 && t.destroyedAt);
      
      if (hasDestroyedTowers) {
        const interval = setInterval(() => {
          // Force re-render to update countdown timers
          setCompound(prev => ({ ...prev }));
        }, 60000); // Update every minute
        
        return () => clearInterval(interval);
      }
    }
  }, [selectedAction, compound]);

  const loadCompoundData = async () => {
    try {
      // Check if user has defense systems before making API call
      const defenseSystemParts = user?.partsInventory?.filter(part => 
        part.name === 'MPU5 Fortress Core' || 
        part.name === 'MUP5 Fortress Core' || // Handle typo in part name
        part.category === 'defense_systems'
      ) || [];
      
      const hasDefenseSystem = defenseSystemParts.length > 0;
      
      if (!hasDefenseSystem) {
        // User doesn't have defense systems, no need to check for compound
        setCompound(null);
        setLoading(false);
        return;
      }
      
      const response = await api.get('/compound-defense/status');
      setCompound(response.data.compound);
      setNearbyThreats(response.data.nearbyCompounds || []);
    } catch (error) {
      // Expected error - user doesn't have a compound yet
      if (error.response?.status === 404 || error.response?.status === 401) {
        setCompound(null);
      } else {
        console.error('Error loading compound data:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const checkLockoutStatus = async () => {
    try {
      // Check user's defense system lockout status
      if (user?.defenseSystemLockout?.isLocked) {
        const lockoutExpiry = new Date(user.defenseSystemLockout.lockoutExpiresAt);
        const now = new Date();
        
        if (lockoutExpiry > now) {
          const hoursRemaining = Math.ceil((lockoutExpiry - now) / (1000 * 60 * 60));
          const movesRemaining = user.defenseSystemLockout.commandCenterMoves - (user.defenseSystemLockout.movesUsed || 0);
          
          setLockoutStatus({
            isLocked: true,
            hoursRemaining: hoursRemaining,
            movesRemaining: movesRemaining,
            totalMoves: user.defenseSystemLockout.commandCenterMoves,
            lockoutExpiresAt: lockoutExpiry
          });
        } else {
          setLockoutStatus(null);
        }
      } else {
        setLockoutStatus(null);
      }
    } catch (error) {
      console.error('Error checking lockout status:', error);
    }
  };

  const moveCommandCenter = async (position) => {
    try {
      const response = await api.post('/compound-defense/move-command-center', {
        position: position
      });
      
      // Update lockout status with new move count
      setLockoutStatus(prev => ({
        ...prev,
        movesRemaining: response.data.movesRemaining
      }));
      
      // Reload compound data to show new position
      await loadCompoundData();
      
      alert(`Command center moved successfully! ${response.data.movesRemaining} moves remaining.`);
      
    } catch (error) {
      console.error('Error moving command center:', error);
      alert(error.response?.data?.message || 'Failed to move command center');
    }
  };

  const loadThreats = async () => {
    setThreatsLoading(true);
    try {
      const response = await api.get('/compound-defense/threats');
      const data = response.data;
      setDetectedThreats(data.threats || []);
    } catch (error) {
      console.error('Error loading threats:', error);
      
      // Handle specific error types for better user feedback
      if (error.response?.status === 403) {
        const errorData = error.response.data;
        if (errorData.requiresPurchase) {
          setDetectedThreats([]);
          // Error message will be shown in the threats display
        } else if (errorData.requiresActivation) {
          setDetectedThreats([]);
          // Error message will be shown in the threats display
        }
      } else {
        setDetectedThreats([]);
      }
    } finally {
      setThreatsLoading(false);
    }
  };

  const loadIncomingAttacks = async () => {
    try {
      const response = await api.get('/drone/active-attacks');
      const data = response.data;
      setIncomingAttacks(data.activeAttacks || []);
    } catch (error) {
      console.error('Error loading incoming attacks:', error);
    }
  };

  const loadUserDrones = async () => {
    try {
      setDronesLoading(true);
      const dronesData = await getUserDrones();
      setUserDrones(dronesData.drones || []);
    } catch (error) {
      console.error('Error loading user drones:', error);
      setUserDrones([]);
    } finally {
      setDronesLoading(false);
    }
  };

  const loadSatelliteData = async () => {
    try {
      setSatellitesLoading(true);
      
      // First check if user has any active satellites
      const satellitesResponse = await getUserSatellites();
      const satellites = (satellitesResponse && satellitesResponse.satellites) || [];
      setUserSatellites(satellites);
      
      if (satellites.length === 0) {
        // No satellites deployed
        setSatelliteData([]);
        return;
      }
      
      // Get scan results for existing satellites
      const response = await getLatestScanResults();
      setSatelliteData(response.scanResults || []);
    } catch (error) {
      console.error('Error loading satellite data:', error);
      setSatelliteData([]);
    } finally {
      setSatellitesLoading(false);
    }
  };

  const handleDeleteDrone = async (droneId) => {
    if (!window.confirm('Are you sure you want to delete this drone? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteDrone(droneId);
      // Refresh the drones list
      await loadUserDrones();
      alert('Drone deleted successfully!');
    } catch (error) {
      console.error('Error deleting drone:', error);
      alert('Failed to delete drone. Please try again.');
    }
  };

  const initializeCompound = async () => {
    if (!user.gameLocation) {
      alert('You need to set your location in Hack The Planet first!');
      return;
    }

    try {
      const response = await api.post('/compound-defense/initialize', {
        commandCenterPosition: {
          lat: user.gameLocation.lat,
          lng: user.gameLocation.lng
        }
      });
      const data = response.data;
      setCompound(data.compound);
      alert('Compound defense system initialized successfully!');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to initialize compound';
      console.error('Error initializing compound:', error);
      alert(`Error: ${errorMessage}`);
    }
  };

  const redirectToHackThePlanet = () => {
    window.location.href = '/hack-the-planet?mode=compound-placement';
  };


  const deployDefenseTower = async (position) => {
    try {
      const response = await api.post('/compound-defense/deploy-tower', {
        position: position,
        towerId: `tower_${Date.now()}`
      });
      const data = response.data;
      loadCompoundData(); // Refresh compound data
      alert(`Defense tower deployed successfully! (${data.totalTowers}/8 towers)`);
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to deploy defense tower';
      console.error('Error deploying tower:', error);
      alert(`Error: ${errorMessage}`);
    }
  };

  const toggleSystem = async (system, active) => {
    try {
      const response = await api.post('/compound-defense/toggle-systems', { system, active });
      loadCompoundData();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Error toggling system';
      console.error('Error toggling system:', error);
      alert(`Error: ${errorMessage}`);
    }
  };


  const deleteDefenseTower = async (towerId) => {
    if (!window.confirm('Are you sure you want to delete this defense tower? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await api.delete(`/compound-defense/delete/defense-tower/${towerId}`);
      const data = response.data;
      loadCompoundData();
      alert(`Defense tower deleted successfully! Remaining towers: ${data.remainingTowers}`);
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete defense tower';
      console.error('Error deleting defense tower:', error);
      alert(`Error: ${errorMessage}`);
    }
  };

  const deleteCommandCenter = async () => {
    if (!window.confirm('Are you sure you want to delete your Command Center? This will disable all defense systems and cannot be undone.')) {
      return;
    }

    try {
      const response = await api.delete('/compound-defense/delete/command-center');
      loadCompoundData();
      alert('Command Center deleted successfully!');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete command center';
      console.error('Error deleting command center:', error);
      alert(`Error: ${errorMessage}`);
    }
  };


  const deleteEntireCompound = async () => {
    if (!window.confirm('⚠️ WARNING: Are you sure you want to delete your ENTIRE COMPOUND? This will remove all defense towers, command center, and radar station. This action CANNOT be undone!')) {
      return;
    }

    if (!window.confirm('This is your final warning. Type "DELETE" in the next prompt to confirm compound deletion.')) {
      return;
    }

    const confirmText = prompt('Type "DELETE" to confirm compound deletion:');
    if (confirmText !== 'DELETE') {
      alert('Compound deletion cancelled.');
      return;
    }

    try {
      const response = await api.delete('/compound-defense/delete/compound');
      loadCompoundData();
      alert('Entire compound deleted successfully!');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete compound';
      console.error('Error deleting compound:', error);
      alert(`Error: ${errorMessage}`);
    }
  };

  if (loading) {
    return (
      <div className="compound-defense-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading compound defense systems...</p>
        </div>
      </div>
    );
  }

  if (!compound) {
    // Check if user has purchased MUP5 Fortress Core but hasn't deployed it
    const hasFortressCore = user?.partsInventory?.some(part => 
      part.name === 'MPU5 Fortress Core' || 
      part.name === 'MUP5 Fortress Core' || // Handle typo in part name
      part.category === 'defense_systems'
    );

    if (hasFortressCore) {
      // Check if user has set a game location (ready to initialize)
      const hasGameLocation = user?.gameLocation?.lat && user?.gameLocation?.lng;
      
      // Show deployment interface for purchased Fortress Core
      return (
        <div className="compound-defense-container">
          <div className="compound-init-container">
            <h1>🚐 MUP5 Fortress Defense System</h1>
            <div className="init-info">
              <h2>Deploy Your Fortress Core</h2>
              <p>✅ You have purchased the MUP5 Fortress Core!</p>
              
              {!hasGameLocation ? (
                <>
                  <p>Now you need to deploy it to establish your compound defense system.</p>
                  
                  <div className="deployment-instructions" style={{ 
                    background: 'rgba(0, 255, 65, 0.1)', 
                    border: '1px solid #00ff41', 
                    borderRadius: '8px', 
                    padding: '15px', 
                    margin: '15px 0' 
                  }}>
                    <h3 style={{ color: '#00ff41', margin: '0 0 10px 0' }}>📍 Deployment Instructions:</h3>
                    <ol style={{ color: '#cccccc', lineHeight: '1.6' }}>
                      <li>Go to Hack The Planet map interface</li>
                      <li>Click anywhere on the map to set location</li>
                      <li>Return here to complete deployment</li>
                    </ol>
                  </div>

                  <div className="deployment-action">
                    <p style={{ color: '#00ff41', marginBottom: '15px', fontWeight: 'bold' }}>
                      🚀 Ready to Deploy Fortress Core
                    </p>
                    <button 
                      className="deploy-btn"
                      onClick={() => window.location.href = '/hack-the-planet?mode=compound-placement'}
                      style={{
                        backgroundColor: '#00ff41',
                        color: '#000',
                        border: 'none',
                        padding: '15px 30px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        borderRadius: '5px',
                        marginBottom: '10px'
                      }}
                    >
                      📍 Go to Hack The Planet
                    </button>
                    <p style={{ fontSize: '14px', color: '#ccc' }}>
                      Choose your compound location on the world map.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <p>✅ Location set at coordinates: {user.gameLocation.lat.toFixed(4)}, {user.gameLocation.lng.toFixed(4)}</p>
                  <p>🚀 Ready to initialize your compound defense system!</p>
                  
                  <div className="initialization-ready" style={{ 
                    background: 'rgba(255, 215, 0, 0.1)', 
                    border: '1px solid #ffd700', 
                    borderRadius: '8px', 
                    padding: '15px', 
                    margin: '15px 0' 
                  }}>
                    <h3 style={{ color: '#ffd700', margin: '0 0 10px 0' }}>🏗️ Initialize Compound:</h3>
                    <p style={{ color: '#cccccc', lineHeight: '1.6', margin: '0' }}>
                      Your MUP5 Fortress Core will be deployed at the selected location. 
                      This will establish your command center and defense perimeter.
                    </p>
                  </div>

                  <div className="initialize-action">
                    <button 
                      className="initialize-btn"
                      onClick={initializeCompound}
                      style={{
                        backgroundColor: '#ffd700',
                        color: '#000',
                        border: 'none',
                        padding: '15px 30px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        borderRadius: '5px',
                        marginBottom: '10px',
                        marginRight: '10px'
                      }}
                    >
                      🏗️ Initialize Compound
                    </button>
                    <button 
                      className="change-location-btn"
                      onClick={() => window.location.href = '/hack-the-planet?mode=compound-placement'}
                      style={{
                        backgroundColor: 'transparent',
                        color: '#00ff41',
                        border: '1px solid #00ff41',
                        padding: '15px 30px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        borderRadius: '5px',
                        marginBottom: '10px'
                      }}
                    >
                      📍 Change Location
                    </button>
                    <p style={{ fontSize: '14px', color: '#ccc' }}>
                      Click Initialize to deploy your fortress, or Change Location to pick a different spot.
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Show shop redirect for users who haven't purchased defense systems
    return (
      <div className="compound-defense-container">
        <div className="compound-init-container">
          <h1>🚐 MUP5 Fortress Defense System</h1>
          <div className="init-info">
            <h2>No Compound Defense System</h2>
            <p>You currently have no compound defense system active.</p>
            <p>To establish a fortified compound, you must purchase defense components from the Cyber Warfare Market:</p>
            <ul>
              <li>🚐 <strong>MUP5 Fortress Core</strong> - Command Center (1,000,000 WR Coins)</li>
              <li>🗼 <strong>MUP5 Defense Tower</strong> - Shield protection (5,000 WR Coins each)</li>
              <li>📡 <strong>MUP5 Radar Station</strong> - Detection systems (250,000 WR Coins)</li>
            </ul>
            <div className="shop-redirect">
              <p style={{ color: '#00ff41', marginBottom: '15px', fontWeight: 'bold' }}>
                ⚡ Purchase defense systems from the Cyber Warfare Market
              </p>
              <button 
                className="shop-btn"
                onClick={() => window.location.href = '/lifestyle'}
                style={{
                  backgroundColor: '#00ff41',
                  color: '#000',
                  border: 'none',
                  padding: '15px 30px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  borderRadius: '5px',
                  marginBottom: '10px'
                }}
              >
                🛒 Go to Cyber Warfare Market
              </button>
              <p style={{ fontSize: '14px', color: '#ccc' }}>
                Visit the Defense Systems section to purchase your compound components.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="compound-defense-container">
      <div className="compound-header">
        <h1>🚐 {user.username}'s Fortress</h1>
        <div className="alert-level">
          Alert Level: <span className={`alert-${compound.combatStatus.alertLevel}`}>
            {compound.combatStatus.alertLevel.toUpperCase()}
          </span>
        </div>
      </div>

      <div className="compound-nav">
        <button 
          className={selectedAction === 'overview' ? 'active' : ''}
          onClick={() => setSelectedAction('overview')}
        >
          📊 Overview
        </button>
        <button 
          className={selectedAction === 'defenses' ? 'active' : ''}
          onClick={() => setSelectedAction('defenses')}
        >
          🗼 Defenses
        </button>
        <button 
          className={selectedAction === 'systems' ? 'active' : ''}
          onClick={() => setSelectedAction('systems')}
        >
          ⚙️ Systems
        </button>
        <button 
          className={selectedAction === 'threats' ? 'active' : ''}
          onClick={() => setSelectedAction('threats')}
          disabled={!compound?.radarStation?.active}
          style={{
            opacity: compound?.radarStation?.active ? 1 : 0.5,
            cursor: compound?.radarStation?.active ? 'pointer' : 'not-allowed'
          }}
        >
          ⚠️ Threats
        </button>
        <button 
          className={selectedAction === 'drones' ? 'active' : ''}
          onClick={() => setSelectedAction('drones')}
        >
          🚁 Drones
        </button>
        <button 
          className={selectedAction === 'mdss' ? 'active' : ''}
          onClick={() => setSelectedAction('mdss')}
        >
          🛰️ MDSS
        </button>
      </div>

      <div className="compound-content">
        {selectedAction === 'overview' && (
          <div className="overview-section">
            <div className="status-grid">
              <div className="status-card">
                <h3>🚐 Command Center</h3>
                <div className="health-bar">
                  <div 
                    className="health-fill"
                    style={{
                      width: `${(compound.commandCenter.health / compound.commandCenter.maxHealth) * 100}%`
                    }}
                  ></div>
                </div>
                <p>{compound.commandCenter.health.toLocaleString()} / {compound.commandCenter.maxHealth.toLocaleString()} HP</p>
                <p>Power: {compound.commandCenter.powerLevel}%</p>
              </div>

              <div className="status-card">
                <h3>🗼 Defense Towers</h3>
                <p>Active: {compound.defenseTowers.filter(t => t.active).length} / {compound.defenseTowers.length}</p>
                <p>Maximum: 8 towers</p>
                <p>Shield Coverage: {compound.defenseTowers.filter(t => t.active).length * 1000}m radius</p>
                {compound.defenseTowers.length < 8 && (
                  <button className="deploy-tower-btn" onClick={() => window.location.href = '/hack-the-planet?mode=tower-placement'}>
                    🗼 Deploy Tower (5,000 WR Coins)
                  </button>
                )}
              </div>

              <div className="status-card">
                <h3>📡 Detection Systems</h3>
                <p>Radar: {compound.radarStation?.active ? '🟢 Online' : '🔴 Offline'}</p>
                <p>Range: {compound.radarStation?.detectionRange / 1000}km</p>
                <p>Nearby Threats: {nearbyThreats.length}</p>
              </div>

            </div>
            
            {/* Initialize/Repair Button when Command Center Health is 0 */}
            {compound.commandCenter.health === 0 && (
              <div className="initialize-section" style={{
                marginTop: '20px',
                padding: '20px',
                background: 'rgba(255, 165, 0, 0.1)',
                border: '2px solid #ffa500',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <h3 style={{ color: '#ffa500', margin: '0 0 15px 0' }}>
                  🔧 Command Center Destroyed
                </h3>
                <p style={{ fontSize: '14px', color: '#ccc', marginBottom: '15px' }}>
                  Your command center has been destroyed. After moving it to a new location, you can initialize it to restore functionality.
                </p>
                <button
                  className="initialize-btn"
                  onClick={async () => {
                    try {
                      const response = await api.post('/compound-defense/repair', {
                        structureType: 'commandCenter',
                        structureId: null,
                        repairAmount: compound.commandCenter.maxHealth
                      });
                      
                      if (response.data.success || response.status === 200) {
                        alert('Command center initialized successfully!');
                        await loadCompoundData();
                      }
                    } catch (error) {
                      alert(error.response?.data?.message || 'Failed to initialize command center');
                    }
                  }}
                  style={{
                    backgroundColor: '#ffa500',
                    color: '#000',
                    border: 'none',
                    padding: '12px 24px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    borderRadius: '5px'
                  }}
                >
                  🔧 Initialize Command Center
                </button>
              </div>
            )}
            
            {/* Defense System Lockout Status */}
            {lockoutStatus && lockoutStatus.isLocked && (
              <div className="lockout-status-section" style={{
                marginTop: '20px',
                padding: '20px',
                background: 'rgba(255, 68, 68, 0.1)',
                border: '2px solid #ff4444',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <h3 style={{ color: '#ff4444', margin: '0 0 15px 0' }}>
                  🔒 DEFENSE SYSTEM LOCKOUT
                </h3>
                <div className="lockout-info" style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                  gap: '15px',
                  color: '#cccccc'
                }}>
                  <div className="lockout-timer">
                    <h4 style={{ color: '#ff4444', margin: '0 0 8px 0' }}>⏰ Time Remaining</h4>
                    <p style={{ fontSize: '18px', fontWeight: 'bold' }}>{lockoutStatus.hoursRemaining} hours</p>
                  </div>
                  <div className="command-center-moves">
                    <h4 style={{ color: '#ffa500', margin: '0 0 8px 0' }}>🚐 Command Center Moves</h4>
                    <p style={{ fontSize: '18px', fontWeight: 'bold' }}>{lockoutStatus.movesRemaining}/{lockoutStatus.totalMoves || 1} remaining</p>
                  </div>
                </div>
                <div className="lockout-actions" style={{ marginTop: '15px' }}>
                  <p style={{ fontSize: '14px', color: '#ccc', marginBottom: '10px' }}>
                    Your command center was destroyed. You can move it {lockoutStatus.movesRemaining} more times 
                    and must wait {lockoutStatus.hoursRemaining} hours before purchasing new defense systems.
                  </p>
                  {lockoutStatus.movesRemaining > 0 && (
                    <button
                      className="move-command-center-btn"
                      onClick={() => window.location.href = '/hack-the-planet?mode=command-center-move'}
                      style={{
                        backgroundColor: '#ffa500',
                        color: '#000',
                        border: 'none',
                        padding: '12px 20px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        borderRadius: '5px'
                      }}
                    >
                      📍 Move Command Center ({lockoutStatus.movesRemaining} moves left)
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {selectedAction === 'defenses' && (
          <div className="defenses-section">
            <h2>🗼 Defense Infrastructure</h2>
            
            <div className="towers-grid">
              {compound.defenseTowers.map((tower, index) => {
                // Calculate time until removal if tower is destroyed
                const isDestroyed = tower.health <= 0 && tower.destroyedAt;
                let timeUntilRemoval = null;
                let removalTimeDisplay = '';
                
                if (isDestroyed) {
                  const destroyedTime = new Date(tower.destroyedAt).getTime();
                  const removeTime = destroyedTime + (12 * 60 * 60 * 1000); // 12 hours later
                  const now = Date.now();
                  timeUntilRemoval = Math.max(0, removeTime - now);
                  
                  if (timeUntilRemoval > 0) {
                    const hours = Math.floor(timeUntilRemoval / (60 * 60 * 1000));
                    const minutes = Math.floor((timeUntilRemoval % (60 * 60 * 1000)) / (60 * 1000));
                    removalTimeDisplay = `${hours}h ${minutes}m`;
                  }
                }
                
                return (
                  <div key={tower.id} className={`tower-card ${isDestroyed ? 'destroyed' : ''}`}>
                    <h4>Tower {index + 1}</h4>
                    <p>Health: {tower.health} / {tower.maxHealth}</p>
                    <p>Status: {tower.active ? '🟢 Active' : '🔴 Offline'}</p>
                    {isDestroyed && timeUntilRemoval > 0 && (
                      <p className="destruction-timer" style={{ color: '#ff4444', fontWeight: 'bold' }}>
                        ⏱️ Removal in: {removalTimeDisplay}
                      </p>
                    )}
                    <p>Shield: {tower.shieldRadius}m radius</p>
                    <div className="tower-actions">
                      <button 
                        className="delete-btn"
                        onClick={() => deleteDefenseTower(tower.id)}
                        style={{ 
                          background: '#ff4444', 
                          color: 'white', 
                          border: 'none', 
                          padding: '8px 12px', 
                          borderRadius: '4px',
                          cursor: 'pointer',
                          marginLeft: '8px'
                        }}
                      >
                        🗑️ Delete Tower
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

          </div>
        )}

        {selectedAction === 'systems' && (
          <div className="systems-section">
            <h2>⚙️ Defense Systems</h2>
            
            <div className="systems-grid">
              <div className="system-card">
                <h3>📡 Radar Station</h3>
                <p>Status: {compound.radarStation?.active ? '🟢 Online' : '🔴 Offline'}</p>
                <p>Detection Range: {compound.radarStation?.detectionRange / 1000}km</p>
                <button 
                  className={`toggle-btn ${compound.radarStation?.active ? 'active' : ''}`}
                  onClick={() => toggleSystem('radar', !compound.radarStation?.active)}
                >
                  {compound.radarStation?.active ? 'Disable' : 'Enable'} Radar
                </button>
              </div>

            </div>
          </div>
        )}

        {selectedAction === 'threats' && (
          <div className="threats-section">
            <h2>⚠️ Defense Perimeter Threats</h2>
            <p className="threat-info">Detecting computers and drones within 1000m of command center and defense towers</p>
            
            {/* Check if user has purchased MUP5 Radar Station */}
            {!user?.partsInventory?.some(part => 
              part.name === 'MUP5 Radar Station' && part.category === 'defense_systems'
            ) && (
              <div className="radar-purchase-required" style={{
                textAlign: 'center',
                padding: '40px',
                background: 'rgba(255, 68, 68, 0.1)',
                border: '2px solid #ff4444',
                borderRadius: '8px',
                color: '#ff4444',
                margin: '20px 0'
              }}>
                <h3 style={{ color: '#ff4444', marginBottom: '15px' }}>
                  🛒 MUP5 Radar Station Purchase Required
                </h3>
                <p style={{ fontSize: '16px', lineHeight: '1.6' }}>
                  You must purchase the <strong>MUP5 Radar Station</strong> from the 🛡️ Defense Systems shop before you can detect threats.
                </p>
                <p style={{ fontSize: '14px', marginTop: '15px', opacity: 0.9 }}>
                  <strong>📍 Location:</strong> Lifestyle → ⚔️ Cyber Warfare Market → 🛡️ Defense Systems
                </p>
                <p style={{ fontSize: '14px', marginTop: '10px', opacity: 0.8 }}>
                  Cost: 250,000 WR Coins + 24,000 XP
                </p>
              </div>
            )}

            {/* Check if radar station needs to be activated */}
            {user?.partsInventory?.some(part => 
              part.name === 'MUP5 Radar Station' && part.category === 'defense_systems'
            ) && !compound?.radarStation?.active && (
              <div className="radar-activation-required" style={{
                textAlign: 'center',
                padding: '40px',
                background: 'rgba(255, 165, 0, 0.1)',
                border: '2px solid #ffa500',
                borderRadius: '8px',
                color: '#ffa500',
                margin: '20px 0'
              }}>
                <h3 style={{ color: '#ffa500', marginBottom: '15px' }}>
                  📡 Radar Station Activation Required
                </h3>
                <p style={{ fontSize: '16px', lineHeight: '1.6' }}>
                  You own the MUP5 Radar Station but it's not currently active. Enable it to detect threats in your defense perimeter.
                </p>
                <p style={{ fontSize: '14px', marginTop: '15px', opacity: 0.9 }}>
                  <strong>📍 Activation:</strong> Go to Systems tab below and enable "📡 Radar Station"
                </p>
              </div>
            )}
            
            {compound?.radarStation?.active && (
              <>
                {/* Defense Tower Status Section */}
                {compound.defenseTowers && compound.defenseTowers.length > 0 && (
              <div className="tower-status-section" style={{ 
                marginBottom: '20px', 
                padding: '15px', 
                background: 'rgba(0, 40, 0, 0.3)', 
                border: '1px solid #00ff41',
                borderRadius: '8px'
              }}>
                <h3 style={{ color: '#00ff41', margin: '0 0 15px 0' }}>
                  🗼 Defense Tower Status ({compound.defenseTowers.length}/8)
                </h3>
                <div className="tower-status-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '10px' }}>
                  {compound.defenseTowers.map((tower, index) => {
                    // Calculate time until removal if tower is destroyed
                    const isDestroyed = tower.health <= 0 && tower.destroyedAt;
                    let timeUntilRemoval = null;
                    let removalTimeDisplay = '';
                    
                    if (isDestroyed) {
                      const destroyedTime = new Date(tower.destroyedAt).getTime();
                      const removeTime = destroyedTime + (12 * 60 * 60 * 1000); // 12 hours later
                      const now = Date.now();
                      timeUntilRemoval = Math.max(0, removeTime - now);
                      
                      if (timeUntilRemoval > 0) {
                        const hours = Math.floor(timeUntilRemoval / (60 * 60 * 1000));
                        const minutes = Math.floor((timeUntilRemoval % (60 * 60 * 1000)) / (60 * 1000));
                        removalTimeDisplay = `${hours}h ${minutes}m`;
                      }
                    }
                    
                    return (
                      <div key={tower.id} style={{
                        background: isDestroyed ? 'rgba(40, 0, 0, 0.4)' : 'rgba(0, 40, 0, 0.2)',
                        border: isDestroyed ? '2px solid #ff4444' : '1px solid #00ff41',
                        borderRadius: '6px',
                        padding: '10px',
                        boxShadow: isDestroyed ? '0 0 10px rgba(255, 68, 68, 0.5)' : 'none'
                      }}>
                        <h4 style={{ 
                          color: isDestroyed ? '#ff4444' : '#00ff41', 
                          margin: '0 0 8px 0',
                          fontSize: '14px' 
                        }}>
                          🗼 Tower {index + 1}
                        </h4>
                        <p style={{ fontSize: '12px', margin: '2px 0' }}>
                          <strong>Health:</strong> {tower.health}/{tower.maxHealth}
                        </p>
                        <p style={{ fontSize: '12px', margin: '2px 0' }}>
                          <strong>Status:</strong> {tower.active ? '🟢 Active' : '🔴 Offline'}
                        </p>
                        {isDestroyed && timeUntilRemoval > 0 && (
                          <p style={{ 
                            color: '#ff4444', 
                            fontWeight: 'bold',
                            fontSize: '12px',
                            background: 'rgba(255, 68, 68, 0.2)',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            margin: '8px 0',
                            animation: 'blink-warning 1s infinite'
                          }}>
                            ⏱️ DELETION IN: {removalTimeDisplay}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Incoming Attacks Section */}
            {incomingAttacks.length > 0 && (
              <div className="incoming-attacks-section" style={{ 
                marginBottom: '20px', 
                padding: '15px', 
                background: 'rgba(255, 68, 68, 0.2)', 
                border: '2px solid #ff4444',
                borderRadius: '8px'
              }}>
                <h3 style={{ color: '#ff4444', margin: '0 0 10px 0' }}>
                  🚨 INCOMING ATTACKS ({incomingAttacks.length})
                </h3>
                <div className="attacks-grid" style={{ display: 'grid', gap: '10px' }}>
                  {incomingAttacks.map((attack) => {
                    const timeRemaining = Math.max(0, attack.timeRemaining);
                    const minutes = Math.floor(timeRemaining / 60);
                    const seconds = timeRemaining % 60;
                    
                    return (
                      <div key={attack.droneId} style={{
                        background: 'rgba(255, 68, 68, 0.1)',
                        border: '1px solid #ff4444',
                        borderRadius: '6px',
                        padding: '10px'
                      }}>
                        <h4 style={{ color: '#ff4444', margin: '0 0 8px 0' }}>
                          ⚔️ {attack.droneType?.toUpperCase() || 'ATTACK'} DRONE ASSAULT
                        </h4>
                        <p><strong>Attacker:</strong> {attack.attackerUsername}</p>
                        <p><strong>Target:</strong> Defense Tower {attack.targetTowerId}</p>
                        <p><strong>Started:</strong> {new Date(attack.battleStartedAt).toLocaleTimeString()}</p>
                        <p style={{ 
                          color: timeRemaining > 600 ? '#ffaa44' : '#ff4444', 
                          fontWeight: 'bold',
                          fontSize: '14px' 
                        }}>
                          {timeRemaining > 0 ? (
                            <>⏰ Tower destroyed in: {minutes}m {seconds}s</>
                          ) : (
                            <>🔥 TOWER DESTROYED</>
                          )}
                        </p>
                        <p style={{ fontSize: '12px', color: '#ccc' }}>
                          Attack Type: {attack.attackType || 'Standard'}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {!compound.radarStation?.active ? (
              <div className="no-radar-warning">
                <p>⚠️ Radar Station Required</p>
                <p className="threat-detail">Activate your radar station to detect threats within the defense perimeter.</p>
              </div>
            ) : threatsLoading ? (
              <p>Scanning for threats...</p>
            ) : detectedThreats.length === 0 ? (
              <div className="no-threats" style={{
                textAlign: 'center',
                padding: '30px',
                background: 'rgba(0, 255, 0, 0.1)',
                border: '2px solid #00ff41',
                borderRadius: '8px',
                color: '#00ff41',
                margin: '20px 0'
              }}>
                <h3 style={{ color: '#00ff41', marginBottom: '15px' }}>
                  ✅ All Clear - No Threats Detected
                </h3>
                <p style={{ fontSize: '16px', lineHeight: '1.6' }}>
                  Your compound is secure. No enemy computers or drones detected within 1000m defense perimeter.
                </p>
                <p style={{ fontSize: '14px', marginTop: '15px', opacity: 0.8 }}>
                  📡 Radar Station actively scanning • 🛡️ Defense systems operational
                </p>
              </div>
            ) : (
              <div className="threats-container">
                <div className="threat-summary" style={{
                  background: 'rgba(255, 68, 68, 0.1)',
                  border: '2px solid #ff4444',
                  borderRadius: '8px',
                  padding: '20px',
                  marginBottom: '20px',
                  textAlign: 'center'
                }}>
                  <h3 style={{ color: '#ff4444', marginBottom: '15px' }}>
                    🚨 <strong>{detectedThreats.length}</strong> Threat{detectedThreats.length > 1 ? 's' : ''} Detected!
                  </h3>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap' }}>
                    <span style={{ color: '#ff4444' }}>
                      <strong>High Risk:</strong> {detectedThreats.filter(t => t.threatLevel === 'high').length}
                    </span>
                    <span style={{ color: '#ffa500' }}>
                      <strong>Medium Risk:</strong> {detectedThreats.filter(t => t.threatLevel === 'medium').length}
                    </span>
                    <span style={{ color: '#ffff00' }}>
                      <strong>Low Risk:</strong> {detectedThreats.filter(t => t.threatLevel === 'low').length}
                    </span>
                  </div>
                  <p style={{ fontSize: '14px', marginTop: '10px', opacity: 0.8 }}>
                    📡 Real-time monitoring active • Updates every 10 seconds
                  </p>
                </div>
                
                <div className="threats-grid">
                  {detectedThreats.map((threat) => (
                    <div key={threat.id} className={`threat-card threat-${threat.threatLevel}`} style={{
                      background: threat.threatLevel === 'high' ? 'rgba(255, 68, 68, 0.1)' : 
                                 threat.threatLevel === 'medium' ? 'rgba(255, 165, 0, 0.1)' : 
                                 'rgba(255, 255, 0, 0.1)',
                      border: `2px solid ${threat.threatLevel === 'high' ? '#ff4444' : 
                                          threat.threatLevel === 'medium' ? '#ffa500' : '#ffff00'}`,
                      borderRadius: '8px',
                      padding: '15px',
                      margin: '10px 0'
                    }}>
                      <h4 style={{ 
                        color: threat.threatLevel === 'high' ? '#ff4444' : 
                               threat.threatLevel === 'medium' ? '#ffa500' : '#ffff00',
                        marginBottom: '10px'
                      }}>
                        {threat.type === 'computer' ? '💻' : '🚁'} 
                        {threat.type === 'computer' ? 'Enemy Computer' : `${threat.droneType} Drone`}
                        <span style={{ 
                          float: 'right', 
                          fontSize: '12px',
                          background: threat.threatLevel === 'high' ? '#ff4444' : 
                                     threat.threatLevel === 'medium' ? '#ffa500' : '#ffff00',
                          color: '#000',
                          padding: '4px 8px',
                          borderRadius: '4px'
                        }}>
                          {threat.threatLevel.toUpperCase()}
                        </span>
                      </h4>
                      <div style={{ lineHeight: '1.6' }}>
                        <p><strong>👤 Owner:</strong> {threat.username}</p>
                        <p><strong>📏 Distance:</strong> {threat.distance}m from perimeter</p>
                        <p><strong>📡 Detected by:</strong> {threat.detectedBy}</p>
                        <p><strong>🕐 Last seen:</strong> Just now (Live)</p>
                        {threat.type === 'drone' && (
                          <>
                            <p><strong>🆔 Drone ID:</strong> {threat.droneId}</p>
                            <p><strong>🔄 Remaining moves:</strong> {threat.moves}</p>
                            <p><strong>⚔️ Type:</strong> {threat.droneType}</p>
                          </>
                        )}
                        {threat.type === 'computer' && (
                          <p><strong>💻 Status:</strong> Active computer placement</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                <div style={{
                  marginTop: '20px',
                  padding: '15px',
                  background: 'rgba(0, 255, 65, 0.1)',
                  border: '1px solid #00ff41',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: '#00ff41'
                }}>
                  <p><strong>📊 Threat Detection Info:</strong></p>
                  <p>• Detection Range: 1000m radius around each defense component</p>
                  <p>• Update Frequency: Every 10 seconds for real-time tracking</p>
                  <p>• Risk Levels: High (&lt;300m) | Medium (300-600m) | Low (600-1000m)</p>
                </div>
              </div>
            )}
            
            <div className="nearby-compounds-section">
              <h3>📡 Nearby Compounds (50km)</h3>
              {nearbyThreats.length === 0 ? (
                <p>No other compounds detected within 50km radius.</p>
              ) : (
                <div className="compounds-grid">
                  {nearbyThreats.map((threat) => (
                    <div key={threat.compoundId || threat._id} className="compound-card">
                      <h4>🏛️ {threat.userId?.username || 'Unknown'}'s Compound</h4>
                      <p>Distance: {threat.distance}km</p>
                      <p>Defense Strength: {threat.defensiveStrength}%</p>
                      <p>Active Towers: {threat.activeTowers}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
              </>
            )}
          </div>
        )}

        {selectedAction === 'drones' && (
          <div className="drones-section">
            <h2>🚁 Active Drones</h2>
            <p className="drones-info">Manage your deployed reconnaissance and assault drones</p>
            
            {dronesLoading ? (
              <div className="loading" style={{ 
                textAlign: 'center', 
                padding: '20px', 
                color: '#00ff41' 
              }}>
                Loading drones...
              </div>
            ) : (
              <>
                {userDrones.length === 0 ? (
                  <div className="no-drones" style={{
                    textAlign: 'center',
                    padding: '40px',
                    background: 'rgba(0, 40, 0, 0.2)',
                    border: '1px solid #00ff41',
                    borderRadius: '8px',
                    color: '#00ff41'
                  }}>
                    <h3>No Active Drones</h3>
                    <p>You don't have any deployed drones.</p>
                    <p>Purchase drones from the Parts Shop → Offense Drones section.</p>
                  </div>
                ) : (
                  <div className="drones-grid" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '15px'
                  }}>
                    {userDrones.map((drone, index) => (
                      <div key={drone.droneId} className="drone-card" style={{
                        background: 'rgba(0, 40, 0, 0.3)',
                        border: '1px solid #00ff41',
                        borderRadius: '8px',
                        padding: '15px',
                        position: 'relative'
                      }}>
                        <div className="drone-header" style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '10px'
                        }}>
                          <h3 style={{ 
                            color: '#00ff41', 
                            margin: 0,
                            fontSize: '16px'
                          }}>
                            {drone.type === 'scout' ? '🔍' : drone.type === 'assault' ? '🚁' : '☢️'} Drone #{index + 1} - {drone.name}
                          </h3>
                          <span className={`drone-status ${drone.status?.active ? 'active' : 'inactive'}`} style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            background: drone.status?.active ? 'rgba(0, 255, 0, 0.2)' : 'rgba(255, 0, 0, 0.2)',
                            color: drone.status?.active ? '#00ff41' : '#ff4444',
                            border: `1px solid ${drone.status?.active ? '#00ff41' : '#ff4444'}`
                          }}>
                            {drone.status?.active ? 'ACTIVE' : 'INACTIVE'}
                          </span>
                        </div>
                        
                        <div className="drone-details">
                          <p style={{ margin: '5px 0', color: '#cccccc', fontSize: '14px' }}>
                            <strong>Type:</strong> {drone.type?.charAt(0).toUpperCase() + drone.type?.slice(1) || 'Unknown'}
                          </p>
                          <p style={{ margin: '5px 0', color: '#cccccc', fontSize: '14px' }}>
                            <strong>Position:</strong> {drone.position?.lat?.toFixed(4)}, {drone.position?.lng?.toFixed(4)}
                          </p>
                          <p style={{ margin: '5px 0', color: '#cccccc', fontSize: '14px' }}>
                            <strong>Battle Status:</strong> {drone.status?.inBattle ? 'In Combat' : 'Ready'}
                          </p>
                          {drone.type === 'scout' && drone.status?.incrementalRecharge?.isActive && (
                            <p style={{ margin: '5px 0', color: '#00ff41', fontSize: '14px' }}>
                              <strong>Recharge Status:</strong> 🔋 Auto-recharging at command center
                            </p>
                          )}
                          {drone.type === 'scout' && drone.status?.needsRecharge && !drone.status?.incrementalRecharge?.isActive && (
                            <p style={{ margin: '5px 0', color: '#ffaa00', fontSize: '14px' }}>
                              <strong>Recharge Status:</strong> ⚠️ Return to command center (1000m radius)
                            </p>
                          )}
                          {drone.status?.movesUsed !== undefined && (
                            <p style={{ margin: '5px 0', color: '#cccccc', fontSize: '14px' }}>
                              <strong>Moves Used:</strong> {drone.status.movesUsed}/{drone.specifications?.moves || 20}
                            </p>
                          )}
                          {drone.specifications && (
                            <div style={{ marginTop: '10px' }}>
                              {drone.type === 'scout' && (
                                <p style={{ margin: '5px 0', color: '#cccccc', fontSize: '12px' }}>
                                  <strong>Sniffer Radius:</strong> {drone.specifications.snifferRadius ? `${(drone.specifications.snifferRadius / 1000).toFixed(1)}km` : '3.0km'}
                                </p>
                              )}
                              {(drone.type === 'assault' || drone.type === 'nuke') && (
                                <p style={{ margin: '5px 0', color: '#cccccc', fontSize: '12px' }}>
                                  <strong>Attack Range:</strong> {drone.specifications.range ? `${drone.specifications.range}m` : (drone.type === 'nuke' ? '1000m' : '500m')}
                                </p>
                              )}
                              <p style={{ margin: '5px 0', color: '#cccccc', fontSize: '12px' }}>
                                <strong>Recharge:</strong> {drone.type === 'scout' ? 'Auto (30min/move at command center)' : 'Single-use drone'}
                              </p>
                            </div>
                          )}
                        </div>
                        
                        <div className="drone-actions" style={{ marginTop: '15px' }}>
                          <button 
                            onClick={() => handleDeleteDrone(drone.droneId)}
                            style={{
                              background: 'rgba(255, 0, 0, 0.2)',
                              border: '1px solid #ff4444',
                              color: '#ff4444',
                              padding: '8px 12px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              width: '100%'
                            }}
                            onMouseOver={(e) => {
                              e.target.style.background = 'rgba(255, 0, 0, 0.4)';
                            }}
                            onMouseOut={(e) => {
                              e.target.style.background = 'rgba(255, 0, 0, 0.2)';
                            }}
                          >
                            🗑️ Delete Drone #{index + 1}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="drones-summary" style={{
                  marginTop: '20px',
                  padding: '15px',
                  background: 'rgba(0, 40, 0, 0.2)',
                  border: '1px solid #00ff41',
                  borderRadius: '8px'
                }}>
                  <h3 style={{ color: '#00ff41', margin: '0 0 10px 0' }}>
                    📊 Fleet Summary
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' }}>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ margin: '5px 0', color: '#cccccc', fontSize: '14px' }}>
                        <strong>Total Drones:</strong> {userDrones.length}
                      </p>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ margin: '5px 0', color: '#cccccc', fontSize: '14px' }}>
                        <strong>Active:</strong> {userDrones.filter(d => d.status?.active).length}
                      </p>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ margin: '5px 0', color: '#cccccc', fontSize: '14px' }}>
                        <strong>In Combat:</strong> {userDrones.filter(d => d.status?.inBattle).length}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {selectedAction === 'mdss' && (
          <div className="mdss-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h2 style={{ margin: 0 }}>🛰️ Maritime Domain Situational Surveillance (MDSS)</h2>
              <button
                onClick={() => setSelectedAction('overview')}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#ff4444',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#ff6666';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = '#ff4444';
                }}
              >
                ✕ Close
              </button>
            </div>
            <p className="mdss-info">Ocean surveillance satellite scan results showing detected naval assets</p>
            
            {satellitesLoading ? (
              <div className="loading" style={{ 
                textAlign: 'center', 
                padding: '20px', 
                color: '#00ff41' 
              }}>
                Loading satellite data...
              </div>
            ) : (
              <>
                {userSatellites.length === 0 ? (
                  <div className="no-satellite-data" style={{
                    textAlign: 'center',
                    padding: '40px',
                    background: 'rgba(0, 40, 0, 0.2)',
                    border: '1px solid #00ff41',
                    borderRadius: '8px',
                    color: '#00ff41'
                  }}>
                    <h3>No Satellite Data Available</h3>
                    <p>You don't have any active Ocean Surveillance Satellites.</p>
                    <p>Purchase an Ocean Surveillance Satellite from the Parts Shop → Defense Systems section.</p>
                    <p>Deploy it in the ocean via Hack The Planet to start collecting naval intelligence.</p>
                  </div>
                ) : (
                  <>
                    {/* Active Satellites Section */}
                    <div className="active-satellites" style={{ marginTop: '20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <h3 style={{ color: '#00ff41', margin: 0 }}>
                          🛰️ Active Satellites ({userSatellites.length})
                        </h3>
                        <button
                          onClick={async () => {
                            try {
                              let successCount = 0;
                              let totalScans = 0;
                              
                              for (const satellite of userSatellites) {
                                if (satellite.canScan) {
                                  try {
                                    await performOceanScan(satellite.id);
                                    successCount++;
                                  } catch (error) {
                                    console.error(`Scan failed for satellite ${satellite.id}:`, error);
                                  }
                                  totalScans++;
                                }
                              }
                              
                              if (successCount > 0) {
                                alert(`✅ Successfully initiated ${successCount} ocean scans! Results will appear below.`);
                                // Refresh satellite data
                                loadSatelliteData();
                              } else if (totalScans === 0) {
                                alert('⏳ All satellites are on cooldown. Wait for next scan availability.');
                              } else {
                                alert(`❌ ${totalScans - successCount} scans failed. Check individual satellite status.`);
                              }
                            } catch (error) {
                              alert('❌ Failed to initiate scans: ' + (error.response?.data?.message || error.message));
                            }
                          }}
                          disabled={userSatellites.length === 0 || !userSatellites.some(sat => sat.canScan)}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: userSatellites.some(sat => sat.canScan) ? '#ffd700' : '#6c757d',
                            color: '#000',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: userSatellites.some(sat => sat.canScan) ? 'pointer' : 'not-allowed',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}
                        >
                          📡 Scan All Available
                        </button>
                      </div>
                      <div className="satellites-grid" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                        gap: '15px',
                        marginBottom: '30px'
                      }}>
                        {userSatellites.map((satellite, index) => (
                          <div key={satellite.id} className="satellite-card" style={{
                            background: 'rgba(0, 40, 0, 0.3)',
                            border: '1px solid #00ff41',
                            borderRadius: '8px',
                            padding: '15px'
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                              <h4 style={{ color: '#00ff41', margin: 0 }}>
                                🛰️ Satellite {index + 1}
                              </h4>
                              <span style={{ 
                                color: satellite.isActive ? '#00ff41' : '#ff4444',
                                fontSize: '12px',
                                fontWeight: 'bold'
                              }}>
                                {satellite.isActive ? '🟢 ACTIVE' : '🔴 INACTIVE'}
                              </span>
                            </div>
                            <div style={{ color: '#cccccc', fontSize: '14px', lineHeight: '1.4' }}>
                              <div>📍 Location: {satellite.location.lat.toFixed(3)}, {satellite.location.lng.toFixed(3)}</div>
                              <div>❤️ Health: {satellite.health}/1000</div>
                              <div>📡 Scan Radius: 1500km</div>
                              <div>🕒 Last Scan: {satellite.lastScanAt ? new Date(satellite.lastScanAt).toLocaleString() : 'Never'}</div>
                              {!satellite.canScan && satellite.nextScanAt && (
                                <div style={{ color: '#ffd700' }}>⏳ Next Scan: {new Date(satellite.nextScanAt).toLocaleString()}</div>
                              )}
                            </div>
                            
                            <button
                              onClick={async () => {
                                try {
                                  const scanData = await performOceanScan(satellite.id);
                                  
                                  // Show detailed scan results
                                  const assetsFound = (scanData.scanResults && scanData.scanResults.assetsFound) || 0;
                                  if (assetsFound > 0) {
                                    alert(`🛰️ Ocean Scan Complete!\n\n✅ Detected ${assetsFound} naval assets within 1500km radius.\n\nCheck the scan results below for detailed intelligence on detected assets.`);
                                  } else {
                                    alert(`🛰️ Ocean Scan Complete!\n\n🌊 No naval assets detected within 1500km radius.\n\nArea appears clear of hostile activity.`);
                                  }
                                  
                                  // Refresh satellite data to show new scan results
                                  await loadSatelliteData();
                                } catch (error) {
                                  console.error('Scan error:', error);
                                  const errorMessage = (error.response && error.response.data && error.response.data.message) || error.message || 'Failed to perform scan';
                                  alert(`❌ Scan Failed\n\n${errorMessage}`);
                                }
                              }}
                              disabled={!satellite.canScan}
                              style={{
                                marginTop: '10px',
                                padding: '8px 15px',
                                backgroundColor: satellite.canScan ? '#28a745' : '#6c757d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: satellite.canScan ? 'pointer' : 'not-allowed',
                                width: '100%',
                                fontSize: '12px',
                                fontWeight: 'bold'
                              }}
                            >
                              {satellite.canScan ? '📡 Perform Ocean Scan' : '⏳ Scan on Cooldown'}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Scan Results Section */}
                    {satelliteData.length === 0 && userSatellites.length > 0 && (
                      <div className="no-scan-results" style={{
                        textAlign: 'center',
                        padding: '30px',
                        background: 'rgba(255, 215, 0, 0.1)',
                        border: '1px solid #ffd700',
                        borderRadius: '8px',
                        color: '#ffd700',
                        marginTop: '20px'
                      }}>
                        <h4 style={{ margin: '0 0 10px 0' }}>📡 No Scan Results Yet</h4>
                        <p style={{ margin: '0 0 15px 0', color: '#cccccc' }}>
                          Your satellites are deployed but haven't performed any scans yet.
                        </p>
                        <p style={{ margin: 0, fontSize: '14px', color: '#888' }}>
                          Use the "📡 Scan All Available" button above or individual scan buttons to start collecting naval intelligence.
                        </p>
                      </div>
                    )}
                    
                    {satelliteData.length > 0 && (
                      <div className="satellite-scans">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                          <h3 style={{ color: '#ffd700', margin: 0 }}>
                            📊 Recent Scan Results
                          </h3>
                          <div style={{ color: '#cccccc', fontSize: '12px' }}>
                            Total Scans: {satelliteData.length} | Assets Detected: {satelliteData.reduce((total, scan) => total + ((scan.navalAssets && scan.navalAssets.length) || 0), 0)}
                          </div>
                        </div>
                        {satelliteData.map((scan, scanIndex) => (
                          <div key={scanIndex} className="scan-result" style={{
                            background: 'rgba(0, 40, 0, 0.3)',
                            border: '1px solid #00ff41',
                            borderRadius: '8px',
                            padding: '20px',
                            marginBottom: '20px'
                          }}>
                            <div className="scan-header" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '15px',
                              paddingBottom: '10px',
                              borderBottom: '1px solid rgba(0, 255, 65, 0.3)'
                            }}>
                              <h3 style={{ color: '#00ff41', margin: 0 }}>
                                📡 Satellite ID: {scan.satelliteId}
                              </h3>
                              <span style={{ color: '#cccccc', fontSize: '14px' }}>
                                Scanned: {new Date(scan.scannedAt).toLocaleString()}
                              </span>
                            </div>
                            
                            {(!scan.navalAssets || scan.navalAssets.length === 0) ? (
                              <div style={{
                                textAlign: 'center',
                                padding: '20px',
                                color: '#888',
                                fontStyle: 'italic'
                              }}>
                                No naval assets detected in scan radius
                              </div>
                            ) : (
                              <div className="naval-assets">
                                <h4 style={{ color: '#ffd700', marginTop: 0, marginBottom: '15px' }}>
                                  🎯 Detected Naval Assets ({(scan.navalAssets && scan.navalAssets.length) || 0})
                                </h4>
                                <div className="assets-grid" style={{
                                  display: 'grid',
                                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                                  gap: '10px'
                                }}>
                                  {scan.navalAssets && scan.navalAssets.map((asset, assetIndex) => (
                                    <div key={assetIndex} className="asset-card" style={{
                                      background: 'rgba(255, 215, 0, 0.1)',
                                      border: '1px solid #ffd700',
                                      borderRadius: '6px',
                                      padding: '12px',
                                      fontSize: '14px'
                                    }}>
                                      <div style={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center',
                                        marginBottom: '8px'
                                      }}>
                                        <div style={{ flex: 1 }}>
                                          <strong style={{ color: '#ffd700' }}>
                                            {asset.type === 'naval_command_center' && '🛥️ Naval Command Center'}
                                            {asset.type === 'naval_defense_tower' && '🗼 Naval Defense Tower'}
                                            {asset.type === 'boat_computer' && '⛵ Boat Computer'}
                                            {asset.type === 'water_atm_vault' && '🏧 Water ATM Vault'}
                                            {asset.type === 'water_house' && `🏠 Water House (${asset.bedrooms || 'N/A'}BR)`}
                                            {asset.type === 'maritime_drone' && `✈️ Maritime Drone (${asset.droneType || 'Unknown'})`}
                                            {asset.type === 'other_satellite' && '🛰️ Enemy Satellite'}
                                            {asset.type === 'fishing_business' && '🎣 Fishing Business'}
                                          </strong>
                                          {/* Action suggestions */}
                                          <div style={{ fontSize: '10px', color: '#888', marginTop: '2px', fontStyle: 'italic' }}>
                                            {asset.type === 'naval_command_center' && 'High-value target - Deploy assault drones'}
                                            {asset.type === 'naval_defense_tower' && 'Defensive structure - Proceed with caution'}
                                            {asset.type === 'boat_computer' && 'Mobile target - May relocate frequently'}
                                            {asset.type === 'water_atm_vault' && 'Secured storage - May contain valuable codes'}
                                            {asset.type === 'water_house' && 'Residential structure - Low priority target'}
                                            {asset.type === 'maritime_drone' && 'Hostile drone - Potential threat'}
                                            {asset.type === 'other_satellite' && 'Enemy surveillance - Consider destruction'}
                                            {asset.type === 'fishing_business' && 'Resource facility - Strategic importance'}
                                          </div>
                                        </div>
                                        <span style={{ color: '#cccccc', fontSize: '12px' }}>
                                          {asset.distance ? `${Math.round(asset.distance)}km` : 'Unknown distance'}
                                        </span>
                                      </div>
                                      <div style={{ color: '#cccccc' }}>
                                        <div>👤 <strong>{asset.username || 'Unknown User'}</strong></div>
                                        {asset.health && (
                                          <div style={{ fontSize: '12px', marginTop: '4px' }}>
                                            ❤️ Health: {asset.health}
                                          </div>
                                        )}
                                        {asset.businessName && (
                                          <div style={{ fontSize: '12px', marginTop: '4px' }}>
                                            🏷️ Name: {asset.businessName}
                                          </div>
                                        )}
                                        {asset.status && (
                                          <div style={{ fontSize: '12px', marginTop: '4px' }}>
                                            📋 Status: {asset.status}
                                          </div>
                                        )}
                                        {asset.approximateLocation && 
                                         typeof asset.approximateLocation.lat === 'number' && 
                                         typeof asset.approximateLocation.lng === 'number' && (
                                          <div style={{ fontSize: '12px', marginTop: '4px' }}>
                                            📍 ~{asset.approximateLocation.lat.toFixed(3)}, {asset.approximateLocation.lng.toFixed(3)}
                                          </div>
                                        )}
                                        {asset.lastSeen && (
                                          <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                                            Last seen: {new Date(asset.lastSeen).toLocaleString()}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
                
                <div className="mdss-info-panel" style={{
                  marginTop: '20px',
                  padding: '15px',
                  background: 'rgba(0, 40, 0, 0.2)',
                  border: '1px solid #00ff41',
                  borderRadius: '8px'
                }}>
                  <h3 style={{ color: '#00ff41', margin: '0 0 10px 0' }}>
                    ℹ️ MDSS Intelligence Notes
                  </h3>
                  <ul style={{ color: '#cccccc', fontSize: '14px', lineHeight: '1.6' }}>
                    <li>🎯 Coordinates are approximate (±5km accuracy) for operational security</li>
                    <li>🕒 Manual scans available every 15 minutes per satellite</li>
                    <li>🚀 Satellites can be moved once every 24 hours</li>
                    <li>🛡️ Satellites are vulnerable to assault drone attacks when discovered</li>
                    <li>📡 1500km scan radius covers vast ocean areas</li>
                    <li>⚠️ Naval assets may move between scan intervals</li>
                    <li>🎣 Fishing businesses and maritime drones are high-value targets</li>
                    <li>🛰️ Enemy satellites can be targeted for destruction</li>
                    <li>🗼 Naval defense towers provide water-based protection</li>
                  </ul>
                </div>
              </>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default CompoundDefense;